/**
 * Générateur — Automatisme : multiplier et diviser par 10, 100, 1 000 ;
 * savoir compléter 1 200 = 1,2 × …
 * Attendu ID : 1102 | Niveau : 4ème | Domaine : Puissances
 * Rubrique : AUTOMATISME (programme 2025, cycle 4 page 9, ligne 676 :
 * « Multiplier et diviser par 10, 100, 1 000 ; savoir compléter
 * 1 200 = 1,2 × … »).
 *
 * ── 1094 (wrapper de gen_1043 → gens 6e 1008 + 1009) + UNE CARTE NEUVE ──
 * La première moitié de la ligne est celle de 1094 (Opérations sur les
 * relatifs, l. 635) : mêmes cartes, mêmes clés produits_* / quotients_*,
 * déléguées telles quelles et re-badgées. La seconde moitié est LE NEUF :
 * le résultat est donné EN PREMIER, le facteur décimal aussi, on cherche la
 * puissance de 10 (« de combien de rangs le nombre a glissé »). gen_1008
 * fait « parfois » 0,45 × {{100}} = 45 en saisie, produit à droite ; la 4e
 * en fait une forme à part entière, résultat à gauche, pré-écriture de
 * a × 10ⁿ (objectif l. 682, 3e l. 752).
 * Frontières : multiplicateur 10, 100, 1 000 seulement (0,1 : objectif 6e
 * l. 904, jamais automatisé ; × 1 exclu de la carte neuve) ; trou sur le
 * facteur (1 200 = … × 1 000 : c'est 1 200 ÷ 1 000, le geste de 1009) et
 * forme division (1,2 = 1 200 ÷ …) hors ligne (choix mode auto, À
 * ARBITRER).
 * MathsMentales (S. Cogez, Apache 2.0, relevé 2026-09-12) : AUCUNE activité
 * sur « 1 200 = 1,2 × … » (4NE10 est l. 710, 4NC5 est l'objectif l. 682) :
 * la carte neuve vient du programme.
 *
 * ── Plan de la carte neuve « Quel nombre manque ? » ────────────────────────
 *   manque_texte_trou_saisie : trois lignes KaTeX « 1 200 = 1,2 × … »,
 *     saisie de 10, 100 ou 1 000 ; un facteur plus petit que 1 par série
 *     (7 = 0,07 × 100, la traversée de l'unité), trois puissances
 *     distinctes.
 *   manque_qcm_unique : « Quel nombre manque ? 1 200 = 1,2 × … », QUATRE
 *     choix : 1 000 (clé), 100 (ajout_zero : compter les zéros du
 *     résultat), 10 000 ou 10 (glissement, un rang de trop ou de moins),
 *     0,001 (sens_inverse) ; 100 < 1 000 < 10 000, la clé encadrée.
 *   manque_tableau_vf : trois égalités vérifiées EN VALEUR (« 1 200 = 1,2 ×
 *     1 000 » V ; « 1 200 = 12 × 100 » V, la décomposition n'est pas
 *     unique ; « 350 = 3,5 × 10 » F).
 *
 * INVARIANTS (carte neuve) : facteur décimal à 1 à 3 chiffres significatifs,
 * 0,01 ≤ facteur < 100, VIRGULE OBLIGATOIRE trois fois sur quatre (avec un
 * facteur entier, compter les zéros donne la bonne réponse : 1 200 = 12 ×
 * 100) ; puissance 10, 100 ou 1 000 ; résultat entier deux fois sur trois
 * (1 200, 350, 47), décimal sinon (4,5 = 0,045 × 100). Arithmétique en
 * MILLIÈMES entiers (zéro flottant), chaque égalité re-vérifiée (throw).
 * SACRIFIÉ (fiche d'aide à venir, le tableau des rangs de 1008 avec la
 * virgule plantée) : (1,2 ; 1 000), l'exemple du programme, sous toutes ses
 * formes dans la carte neuve (auto-testé ; alternative 4,25 de la fiche
 * 1008, À ARBITRER).
 *
 * ── Erreurs (noms de 1008/1009 ; chiffres DEPP 4e 2023 q.12, le même geste
 * de rangs « 43 milliers = … dizaines » : 4 300 réussi 57,6 %, 430 = 18,8 %,
 * 4,3 = 11,1 %, 43 = 10,3 %) ──────────────────────────────────────────────
 *   ajout_zero    : compter les zéros du résultat (1 200 = 1,2 × 100),
 *                   PRINCIPALE, d'où la virgule obligatoire ;
 *   glissement    : un rang de trop ou de moins (10 000 ; 10) ;
 *   sens_inverse  : 0,001 (« 1 200 ÷ 1 000 ») ;
 *   nombre_recopie : en appoint, jamais au QCM.
 */
import {
    randomChoice, randInt, shuffle, formatNumber,
    makeQcm, makeVraiFaux, makeTexteTrou, tex, retirerCodesErreur,
} from '../../../utils';
import { generate as genererDecalage, CATEGORIES as CAT_DECALAGE } from '../../5eme/operations/gen_1043';

const ATTENDU_ID = 1102;
const opts = (label) => ({ niveau: '4eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const L_MANQUE = 'Quel nombre manque ?';

const PUISSANCES = [10, 100, 1000];

// ─── Le domaine de la carte neuve (millièmes entiers) ─────────────────────

/** L'exemple du programme et de la fiche : 1,2 × 1 000, sacrifié. facteur en millièmes. */
export const SACRIFIE = { facteur: 1200, puissance: 1000 };
const estSacrifie = (f, p) => f === SACRIFIE.facteur && p === SACRIFIE.puissance;

/** millièmes → écriture française : 1200 → « 1,2 », 70 → « 0,07 », 47000 → « 47 ». */
const fr = (m) => {
    const ent = Math.floor(m / 1000);
    const dec = String(m % 1000).padStart(3, '0').replace(/0+$/, '');
    return dec ? `${ent},${dec}` : String(ent);
};
/** Idem en KaTeX (virgule décimale sans espace après). */
const frTex = (m) => fr(m).replace(',', '{,}');

/** « 1,2 » → millièmes (le second chemin de l'assert). */
const relire = (s) => {
    const m = String(s).match(/^(\d+)(?:,(\d{1,3}))?$/);
    if (!m) throw new Error(`gen_1102 : écriture illisible ${s}`);
    return parseInt(m[1], 10) * 1000 + parseInt(((m[2] || '') + '000').slice(0, 3), 10);
};

/** Un item { facteur, puissance, resultat } en millièmes ; assert de l'égalité. */
const verifier = (facteur, puissance) => {
    if (!Number.isInteger(facteur) || facteur < 10 || facteur >= 100000 || !PUISSANCES.includes(puissance)) {
        throw new Error(`gen_1102 : hors domaine ${facteur} x ${puissance}`);
    }
    if (estSacrifie(facteur, puissance)) throw new Error('gen_1102 : item sacrifié tiré');
    const resultat = facteur * puissance;
    if (relire(fr(facteur)) * puissance !== resultat || relire(fr(resultat)) !== resultat) throw new Error('gen_1102 : arithmétique');
    return { facteur, puissance, resultat };
};

/**
 * Un facteur : 1 à 3 chiffres significatifs, 0,01 ≤ f < 100 ; `virgule`
 * impose un facteur non entier ; `sous1` impose f < 1 ; `entier` impose un
 * résultat entier (ou non).
 */
const tirer = ({ virgule = true, sous1 = null, entier = true, puissance = randomChoice(PUISSANCES) } = {}) => {
    for (let essai = 0; essai < 200; essai++) {
        const s = randInt(1, 3);
        let m = randInt(10 ** (s - 1), 10 ** s - 1);
        if (s > 1 && m % 10 === 0) continue;
        // e = rang du dernier chiffre significatif : f = m × 10^e, en millièmes m × 10^(e + 3)
        const e = randInt(-3, 1);
        const f = m * 10 ** (e + 3);
        if (f < 10 || f >= 100000) continue;
        if (virgule && f % 1000 === 0) continue;
        if (f === 1000) continue; // « 10 = 1 × 10 » : le facteur 1 ne fait rien glisser
        if (sous1 === true && f >= 1000) continue;
        if (sous1 === false && f < 1000) continue;
        const r = f * puissance;
        if (entier && r % 1000 !== 0) continue;
        if (!entier && r % 1000 === 0) continue;
        if (estSacrifie(f, puissance)) continue;
        return verifier(f, puissance);
    }
    return verifier(3500, 100);
};

/** Virgule trois fois sur quatre ; résultat entier deux fois sur trois (un facteur entier, ou × 1 000 avec trois décimales au plus, donne toujours un résultat entier). */
const tirerLibre = (contraintes = {}) => {
    const virgule = randInt(1, 4) !== 1;
    const puissance = contraintes.puissance ?? randomChoice(PUISSANCES);
    const entier = !virgule || puissance === 1000 || randInt(1, 3) !== 1;
    return tirer({ ...contraintes, puissance, virgule, entier });
};

const EXPLICATIONS = {
    ajout_zero: 'On ne compte pas les zéros : de 1,2 à 1 200, chaque chiffre a glissé de trois rangs, c\'est × 1 000.',
    glissement: 'Un rang de trop ou de moins : compte les rangs entre 1,2 et 1 200.',
    sens_inverse: 'Le résultat est plus grand que le facteur : on a multiplié, pas divisé.',
    regle: 'Pour trouver ce qui manque, je compte de combien de rangs les chiffres ont glissé : trois rangs, c\'est × 1 000.',
};

// ─── Codes d'erreur (contenu.erreurs, lus par trouverErreurs) ─────────────

/** Le code commun de chaque famille (catalogue validé) ; ajout_zero de la carte neuve compte les zéros du résultat : la puissance voisine. Compléter 1 200 = 1,2 × … a son propre code depuis le lot 2 (04/10/2026, docs/SFD_CODES_ERREUR.md). */
const CODE_MANQUE = { ajout_zero: 'rangs_faux_en_completant', glissement: 'rangs_faux_en_completant', sens_inverse: 'sens_du_glissement_inverse' };
/** Les familles de 1008 et 1009 (cartes produits_* et quotients_*). */
const CODE_DECALAGE = {
    ajout_zero: 'ajout_zero', glissement_insuffisant: 'nombre_de_rangs_faux', glissement_excessif: 'nombre_de_rangs_faux',
    sens_inverse: 'sens_du_glissement_inverse', routine_appliquee: 'routine_appliquee',
};

/** Clé de comparaison d'une réponse : sa valeur si c'est un nombre (0,10 = 0.1), son texte sinon. */
const cleReponse = (v) => {
    const s = String(v).replace(/\s/g, '').replace(',', '.');
    return s !== '' && Number.isFinite(Number(s)) ? `n${Number(s)}` : `t${s.toLowerCase()}`;
};

/** Les erreurs nommables parmi des candidats { code, valeur } : jamais la bonne réponse, jamais une valeur que deux codes donnent. */
const nommer = (candidats, bonne) => {
    const groupes = new Map();
    candidats.forEach((c) => {
        const k = cleReponse(c.valeur);
        if (k !== cleReponse(bonne)) groupes.set(k, [...(groupes.get(k) || []), c]);
    });
    return [...groupes.values()]
        .filter((g) => g[0].code && g.every((c) => c.code === g[0].code))
        .map((g) => ({ code: g[0].code, valeur: String(g[0].valeur) }));
};

/** Range les erreurs nommées dans la question (rien si aucune). */
const ranger = (payload, erreurs) => {
    if (erreurs.length) payload.contenu = { ...payload.contenu, erreurs };
    return payload;
};

/** Carte neuve : les familles du QCM, toutes (10^zéros du résultat, la puissance voisine des deux côtés, l'inverse). */
const famillesManque = (it) => {
    const { puissance, resultat } = it;
    const z = zerosFinaux(resultat);
    const f = [{ code: CODE_MANQUE.glissement, valeur: String(puissance * 10) }, { code: CODE_MANQUE.sens_inverse, valeur: fr(1000 / puissance) }];
    if (z !== null && z >= 1) f.push({ code: CODE_MANQUE.ajout_zero, valeur: String(10 ** z) });
    if (puissance / 10 >= 10) f.push({ code: CODE_MANQUE.glissement, valeur: String(puissance / 10) });
    return nommer(f, String(puissance));
};

/** Les leurres d'un QCM ({ valeur, code du gen }) dont l'erreur est nommable : le texte exact du choix, son code commun. */
const erreursQcm = (leurres, codes, nommees) => leurres
    .map((l) => ({ code: codes[l.code], valeur: String(l.valeur) }))
    .filter((l) => l.code && nommees.some((e) => e.code === l.code && cleReponse(e.valeur) === cleReponse(l.valeur)));

// ─── Carte neuve, format A : saisie (3 lignes) ────────────────────────────

const ligneManque = (it) => `$${frTex(it.resultat)} = ${frTex(it.facteur)} \\times {{${it.puissance}}}$`;

const assemblerSaisie = (items) => {
    const payload = makeTexteTrou(items.map(ligneManque).join('\n'), '<p><b>Complète.</b></p>', { ...opts(L_MANQUE), mode: 'saisie' });
    payload.contenu = { ...payload.contenu, explications: { regle: EXPLICATIONS.regle, ajout_zero: EXPLICATIONS.ajout_zero } };
    // une ligne, un trou : les familles du QCM pour la puissance de chaque ligne
    ranger(payload, items.flatMap((it, trou) => famillesManque(it).map(({ code, valeur }) => ({ code, trou, valeur }))));
    payload._meta.params = { items };
    return payload;
};

const genSaisie = () => {
    for (let essai = 0; essai < 80; essai++) {
        const puissances = shuffle([...PUISSANCES]);
        const items = [
            tirer({ puissance: puissances[0], sous1: true }),                 // la traversée de l'unité
            tirer({ puissance: puissances[1], sous1: false }),
            tirerLibre({ puissance: puissances[2] }),
        ];
        if (new Set(items.map((it) => it.facteur)).size < 3) continue;
        return assemblerSaisie(shuffle(items));
    }
    return assemblerSaisie([verifier(70, 100), verifier(3500, 10), verifier(47000, 1000)]);
};

// ─── Carte neuve, format B : QCM ──────────────────────────────────────────

/** Nombre de zéros finaux d'un résultat entier (en millièmes) ; null si décimal. */
const zerosFinaux = (r) => {
    if (r % 1000 !== 0) return null;
    let n = r / 1000;
    let z = 0;
    while (n % 10 === 0 && n > 0) { n /= 10; z++; }
    return z;
};

const distracteurs = (it) => {
    const { puissance, resultat } = it;
    const cands = [];
    const z = zerosFinaux(resultat);
    if (z !== null && z >= 1 && 10 ** z !== puissance) cands.push({ valeur: String(10 ** z), code: 'ajout_zero' });
    for (const g of [puissance * 10, puissance / 10]) if (g >= 10) cands.push({ valeur: String(g), code: 'glissement' });
    cands.push({ valeur: fr(1000 / puissance), code: 'sens_inverse' }); // 1/P en millièmes : 0,1 ; 0,01 ; 0,001
    const vus = new Set([String(puissance)]);
    const choix = cands.filter((c) => { if (vus.has(c.valeur)) return false; vus.add(c.valeur); return true; });
    return choix.length >= 3 ? choix.slice(0, 3) : null;
};

const genQcm = () => {
    for (let essai = 0; essai < 80; essai++) {
        const it = tirerLibre();
        const faux = distracteurs(it);
        if (!faux) continue;
        const bon = String(it.puissance);
        const payload = makeQcm([bon, ...faux.map((x) => x.valeur)], bon, `<p><b>Quel nombre manque ?</b> ${tex(`${frTex(it.resultat)} = ${frTex(it.facteur)} \\times \\ldots`)}</p>`, opts(L_MANQUE));
        payload.contenu = { ...payload.contenu, explications: EXPLICATIONS };
        ranger(payload, erreursQcm(faux, CODE_MANQUE, famillesManque(it)));
        payload._meta.params = { ...it, distracteurs: faux.map(({ code }) => ({ code })) };
        return payload;
    }
    const it = verifier(3500, 100);
    const payload = makeQcm(['100', '10', '1000', '0,01'], '100', `<p><b>Quel nombre manque ?</b> ${tex('350 = 3{,}5 \\times \\ldots')}</p>`, opts(L_MANQUE));
    payload.contenu = { ...payload.contenu, explications: EXPLICATIONS };
    ranger(payload, erreursQcm(distracteurs(it), CODE_MANQUE, famillesManque(it)));
    payload._meta.params = { ...it, distracteurs: [{ code: 'ajout_zero' }, { code: 'glissement' }, { code: 'sens_inverse' }] };
    return payload;
};

// ─── Carte neuve, format C : Vrai ou faux (3 égalités en valeur) ──────────

const egalite = (r, f, p) => tex(`${frTex(r)} = ${frTex(f)} \\times ${p}`);

/** Vrai/faux : chaque égalité fausse sous son code, quand sa puissance est une erreur nommable de l'item. */
const erreursVf = (items, lignes) => lignes.flatMap((l, i) => {
    const p = (l.ligne.match(/\\times (\d+)\$$/) || [])[1];
    const code = CODE_MANQUE[l.code];
    return code && famillesManque(items[i]).some((e) => e.code === code && cleReponse(e.valeur) === cleReponse(p)) ? [{ code, ligne: l.ligne }] : [];
});

const genVf = () => {
    for (let essai = 0; essai < 80; essai++) {
        const items = [tirer({ sous1: false }), tirer({ sous1: true }), tirer({ virgule: false })];
        if (new Set(items.map((it) => it.resultat)).size < 3) continue;
        const nbVraies = randomChoice([1, 2]);
        const verites = shuffle([...Array(nbVraies).fill(true), ...Array(3 - nbVraies).fill(false)]);
        const lignes = items.map((it, i) => {
            if (verites[i]) {
                // une fois sur deux, une autre décomposition juste (1 200 = 12 × 100)
                if (it.puissance > 10 && it.facteur !== 100 && randomChoice([true, false])) return { ligne: egalite(it.resultat, it.facteur * 10, it.puissance / 10), code: null };
                return { ligne: egalite(it.resultat, it.facteur, it.puissance), code: null };
            }
            const faux = distracteurs(it);
            if (!faux) return null;
            const d = randomChoice(faux.filter((x) => x.code !== 'sens_inverse'));
            if (!d) return null;
            return { ligne: egalite(it.resultat, it.facteur, Number(d.valeur)), code: d.code };
        });
        if (lignes.some((l) => !l) || new Set(lignes.map((l) => l.ligne)).size < 3) continue;
        // relecture de chaque ligne AFFICHÉE : étiquette V/F juste, et le couple
        // sacrifié (1,2 ; 1 000) jamais montré, même dans une ligne fausse
        // (« 12 = 1,2 × 1 000 ») ou dans l'autre décomposition d'une ligne vraie
        const lues = lignes.map((l) => {
            const m = l.ligne.replace(/\\mathrm\{\}/g, '').replace(/\{,\}/g, ',').replace(/[$]/g, '').match(/^(\S+) = (\S+) \\times (\d+)$/);
            if (!m) throw new Error(`gen_1102 : ligne illisible ${l.ligne}`);
            return { r: relire(m[1]), f: relire(m[2]), p: Number(m[3]) };
        });
        if (lues.some((x) => estSacrifie(x.f, x.p))) continue;
        lues.forEach((x, i) => {
            if ((x.f * x.p === x.r) !== verites[i]) throw new Error(`gen_1102 : ligne mal étiquetée ${lignes[i].ligne}`);
        });
        const payload = makeVraiFaux(lignes.map((l) => l.ligne), verites.map((v) => (v ? 0 : 1)), '<p><b>Vrai ou faux ?</b></p>', opts(L_MANQUE));
        payload.contenu = { ...payload.contenu, explications: EXPLICATIONS };
        ranger(payload, erreursVf(items, lignes));
        payload._meta.params = { items, verites, distracteurs: lignes.filter((l) => l.code).map(({ code }) => ({ code })) };
        return payload;
    }
    const items = [verifier(3500, 100), verifier(70, 100), verifier(47000, 10)];
    const lignes = [egalite(350000, 3500, 100), egalite(7000, 70, 10), egalite(470000, 47000, 10)];
    const payload = makeVraiFaux(lignes, [0, 1, 0], '<p><b>Vrai ou faux ?</b></p>', opts(L_MANQUE));
    payload.contenu = { ...payload.contenu, explications: EXPLICATIONS };
    ranger(payload, erreursVf(items, lignes.map((ligne, i) => ({ ligne, code: i === 1 ? 'glissement' : null }))));
    payload._meta.params = { items, verites: [true, false, true], distracteurs: [{ code: 'glissement' }] };
    return payload;
};

// ─── Contrat distracteurs (référence de revue) ────────────────────────────

export const REGLES_DISTRACTEURS_QCM = {
    invariants_communs: [
        'cartes produits_* et quotients_* : le contrat de gen_1008 et gen_1009 (via 1043, 1094)',
        'carte neuve : quatre choix distincts, la clé encadrée (100 < 1 000 < 10 000) ; virgule obligatoire trois fois sur quatre, sinon compter les zéros donne la clé',
        'sacrifié : (1,2 ; 1 000) sous toutes ses formes dans la carte neuve',
    ],
    qcm_unique: {
        support: '« R = f × … », f décimal à 1-3 chiffres significatifs (0,01 ≤ f < 100), puissance 10, 100 ou 1 000, résultat entier deux fois sur trois',
        familles: {
            ajout_zero: '10^(zéros finaux du résultat) : 1 200 = 1,2 × 100, la principale, retenue seulement quand elle diffère de la clé',
            glissement: 'un rang de trop ou de moins (10 000 ; 10 ; DEPP 4e 2023 q.12 : 430 pour 4 300, 18,8 %)',
            sens_inverse: '0,001 : « 1 200 ÷ 1 000 » (DEPP q.12 : 4,3 pour 4 300, 11,1 % ; DEPP 6e 2025 q.14, 18,8 %)',
        },
    },
    vf_tableau_vf: 'trois égalités vérifiées en valeur ; une vraie sur deux est une autre décomposition juste (1 200 = 12 × 100) ; fausses : ajout_zero ou glissement',
};

// ─── Cartes de 1008 et 1009 (produits_*, quotients_*) : les erreurs relues sur la question ───

/** « 1 250 », « 0,85 » → { ent, dec } ; null si l'écriture n'est pas un décimal. */
const lireDecimal = (s) => {
    const m = String(s).replace(/\s/g, '').match(/^(\d+)(?:,(\d+))?$/);
    return m ? { ent: m[1], dec: m[2] || '' } : null;
};

/** a × 10^k par glissement de chaîne (k < 0 : division), écriture canonique, comme 1008 et 1009. */
const glisser = (n, k) => {
    let digits = n.ent + n.dec;
    const newDec = n.dec.length - k;
    if (newDec <= 0) return (digits + '0'.repeat(-newDec)).replace(/^0+(?=\d)/, '');
    while (digits.length <= newDec) digits = `0${digits}`;
    const ent = digits.slice(0, -newDec).replace(/^0+(?=\d)/, '');
    const dec = digits.slice(-newDec).replace(/0+$/, '');
    return dec ? `${ent},${dec}` : ent;
};

/** Les erreurs de « a × 10^q » (1008) ou « a ÷ 10^q » (1009), v(k) : k rangs dans le bon sens ; les familles de leurs QCM et les pièges de leurs étiquettes. */
const famillesDecalage = (op, a, q) => {
    const fois = op === '×';
    const v = (k) => glisser(a, fois ? k : -k);
    if (q === 0) return [1, -1, 2].map((k) => ({ code: 'routine_appliquee', valeur: v(k) }));
    const f = [q + 1, ...(fois ? [q + 2] : []), ...(q >= 2 ? [q - 1] : [])].map((k) => ({ code: 'nombre_de_rangs_faux', valeur: v(k) }));
    [q, q + 1, ...(fois ? [] : [q + 2])].forEach((k) => f.push({ code: 'sens_du_glissement_inverse', valeur: v(-k) }));
    if (fois && a.dec) f.push({ code: 'ajout_zero', valeur: `${a.ent},${a.dec}0` });
    return f;
};

/** Une ligne « a × m = r » (ou ÷) de 1008 ou 1009 : l'opération, a, le rang q de m, m en trou ou non, le membre de droite. */
const lireLigne = (ligne) => {
    const m = String(ligne).match(/^(.+?) ([×÷]) (.+?) = (.+)$/);
    if (!m) return null;
    const trouM = m[3].match(/^\{\{(\d+)\}\}$/);
    const q = ['1', '10', '100', '1000'].indexOf((trouM ? trouM[1] : m[3]).replace(/\s/g, ''));
    const a = lireDecimal(m[1]);
    return q >= 0 && a ? { op: m[2], a, q, trouM: Boolean(trouM), droite: m[4] } : null;
};

/** Les erreurs nommables d'une ligne : la puissance voisine quand m est le trou, sinon les familles de a × 10^q, sans ajout_zero en saisie tolérante (3,70 y vaut 3,7 recopié). */
const erreursLigne = (l, bonne, tolerante = false) => (l.trouM
    ? nommer([l.q - 2, l.q - 1, l.q + 1, l.q + 2].filter((k) => k >= 1 && k <= 4).map((k) => ({ code: 'nombre_de_rangs_faux', valeur: String(10 ** k) })), bonne)
    : nommer(famillesDecalage(l.op, l.a, l.q).filter((f) => !tolerante || f.code !== 'ajout_zero'), bonne));

/** Texte d'une étiquette ou d'un membre, sans espaces ni casse. */
const texteNu = (s) => String(s).replace(/\s/g, '').toLowerCase();

/** Carte déléguée (rien si 1008 ou 1009 les range déjà) : QCM, les leurres codés de _meta écrits comme le choix ; trous, une ligne un trou (au glisser, les étiquettes seules) ; vrai/faux, le membre de droite mot pour mot. */
const nommerDecalage = (payload) => {
    const c = payload.contenu || {};
    const cle = payload._meta?.generatorKey || '';
    if (c.erreurs || !/^(produits|quotients)_/.test(cle)) return payload;
    const op = cle.startsWith('produits') ? '×' : '÷';
    const table = (payload.elements_additionnels?.elements || []).find((e) => e.type === 'table');
    if (cle.endsWith('_qcm_unique')) {
        const p = payload._meta.params || {};
        const a = lireDecimal(p.a);
        if (!a) return payload;
        const nommees = nommer(famillesDecalage(op, a, p.q), glisser(a, op === '×' ? p.q : -p.q));
        return ranger(payload, (p.distracteurs || [])
            .map((d) => ({ code: CODE_DECALAGE[d.code], brut: String(d.valeur) }))
            .map((d) => ({ ...d, valeur: d.brut.includes(',') ? d.brut : formatNumber(Number(d.brut)) }))
            .filter((d) => d.code && (c.choix || []).includes(d.valeur) && nommees.some((e) => e.code === d.code && cleReponse(e.valeur) === cleReponse(d.brut)))
            .map(({ code, valeur }) => ({ code, valeur })));
    }
    if (c.subType === 'texte_trou') {
        const tuiles = new Map((c.distracteurs || []).map((t) => [texteNu(t), t]));
        return ranger(payload, String(c.phrase || '').split('\n').flatMap((ligne, trou) => {
            const l = lireLigne(ligne);
            const bonne = (ligne.match(/\{\{([^{}]*)\}\}/) || [])[1];
            if (!l || bonne === undefined) return [];
            return erreursLigne(l, bonne, c.mode === 'saisie' && !c.strict)
                .filter((e) => c.mode !== 'drag' || tuiles.has(texteNu(e.valeur)))
                .map(({ code, valeur }) => ({ code, trou, valeur: c.mode === 'drag' ? tuiles.get(texteNu(valeur)) : valeur }));
        }));
    }
    if (table) {
        const { lignes = [], bonnesReponses = [] } = table.config || {};
        return ranger(payload, lignes.flatMap((ligne, i) => {
            const l = lireLigne(ligne);
            if (bonnesReponses[i] !== 1 || !l) return [];
            const codes = new Set(erreursLigne(l, glisser(l.a, l.op === '×' ? l.q : -l.q))
                .filter((e) => texteNu(e.valeur) === texteNu(l.droite)).map((e) => e.code));
            return codes.size === 1 ? [{ code: [...codes][0], ligne }] : [];
        }));
    }
    return payload;
};

// ─── Registre et point d'entrée ───────────────────────────────────────────

const GENERATORS = {
    manque_texte_trou_saisie: genSaisie,
    manque_qcm_unique: genQcm,
    manque_tableau_vf: genVf,
};

const rebadger = (payload) => {
    payload.niveau = '4eme';
    payload.attendu = ATTENDU_ID;
    return nommerDecalage(payload);
};

export const CATEGORIES = [
    ...CAT_DECALAGE,
    { id: 'manque', label: L_MANQUE, formatsList: [
        { key: 'manque_texte_trou_saisie', label: 'Saisie trous' },
        { key: 'manque_qcm_unique', label: 'QCM' },
        { key: 'manque_tableau_vf', label: 'Vrai/Faux' },
    ] },
];

// Relecture du 04/10/2026 (docs/SFD_CODES_ERREUR.md) : on ne garde un code que sur les cartes où le juge
// l'a lu. Celles-ci ne lui ont jamais été montrées : le choix reste proposé, il part sans nom.
const CODES_RETIRES = {
    produits_qcm_unique: ['nombre_de_rangs_faux'],
    produits_tableau_vf: ['nombre_de_rangs_faux'],
    quotients_qcm_unique: ['nombre_de_rangs_faux'],
    quotients_tableau_vf: ['nombre_de_rangs_faux'],
    manque_tableau_vf: ['rangs_faux_en_completant'],
};
const retirer = (payload) => retirerCodesErreur(payload, CODES_RETIRES[payload._meta?.generatorKey]);

export const generate = (typeExercice) => {
    if (typeExercice && GENERATORS[typeExercice]) {
        const payload = GENERATORS[typeExercice]();
        payload._meta.generatorKey = typeExercice;
        return retirer(payload);
    }
    if (typeExercice && typeExercice.startsWith('manque')) {
        const key = randomChoice(Object.keys(GENERATORS));
        const payload = GENERATORS[key]();
        payload._meta.generatorKey = key;
        return retirer(payload);
    }
    if (typeExercice) return retirer(rebadger(genererDecalage(typeExercice)));
    // sans clé : la carte neuve une fois sur trois, les cartes de 1043 sinon
    if (randInt(1, 3) === 1) return generate('manque');
    return retirer(rebadger(genererDecalage()));
};
